import { useState, useCallback, memo } from "react";
import { Link } from "react-router-dom";
import { FastForward } from "lucide-react";
import { TopBar } from "./top-bar";
import { Sidebar } from "./sidebar";
import { MobileMenu } from "./mobile-menu";
import { RetentionBanner } from "./retention-banner";
import { H3PromoBanner } from "@/components/promo/h3-promo-banner";
import { useH3Promo } from "@/hooks/use-h3-promo";

interface MainLayoutProps {
  children: React.ReactNode;
}

export const MainLayout = memo(function MainLayout({
  children,
}: MainLayoutProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isAnnounced: h3PromoAnnounced } = useH3Promo();

  const handleOpenMenu = useCallback(() => setIsMobileMenuOpen(true), []);
  const handleCloseMenu = useCallback(() => setIsMobileMenuOpen(false), []);

  return (
    <div className="h-full flex flex-col overflow-hidden bg-background noise-overlay">
      <RetentionBanner />
      {h3PromoAnnounced ? (
        <H3PromoBanner />
      ) : (
        <Link
          to="/create/extend-video"
          className="shrink-0 z-40 flex items-center justify-center gap-2 bg-[#e8822e] text-white text-center text-[12px] font-semibold py-1.5 px-4 hover:bg-[#d97620] transition-colors"
        >
          <FastForward className="size-3.5 shrink-0" />
          New — Extend videos automatically stitch together for unlimited-length videos
        </Link>
      )}
      <div className="shrink-0 z-40">
        <TopBar onMenuClick={handleOpenMenu} />
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        <div className="hidden md:block h-full shrink-0 overflow-hidden">
          <Sidebar />
        </div>
        <main
          className="flex-1 overflow-y-auto overflow-x-hidden overscroll-none"
          style={{ overscrollBehavior: "none" }}
        >
          <div className="animate-page-in">
            {children}
          </div>
          <footer className="border-t border-[rgba(45,49,39,0.06)] px-5 md:px-8 py-5">
            <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] text-[#b5b0a0]">
              <span>&copy; {new Date().getFullYear()} CouchStack</span>
              <div className="flex items-center gap-4">
                <Link to="/terms" className="hover:text-[#5a5545] transition-colors">Terms of Service</Link>
                <Link to="/privacy" className="hover:text-[#5a5545] transition-colors">Privacy Policy</Link>
                <Link to="/support" className="hover:text-[#5a5545] transition-colors">Support</Link>
              </div>
            </div>
          </footer>
        </main>
      </div>

      <MobileMenu isOpen={isMobileMenuOpen} onClose={handleCloseMenu} />
    </div>
  );
});
